import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { EncounterRecord } from '@models/encounter';
import type { GetFhirRecordParams } from '@models/fhir-resource';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useGetEmergencyVisitQuery,
  useGetEmergencyVisitsRecordQuery,
  usePostEmergencyVisitsRecordMutation,
  useUpdateEmergencyVisitsEncounterRecordMutation,
  useDeleteEmergencyVisitRecordMutation,
} from 'generated/emergency-visit.generated';
import type {
  PostEmergencyVisitsRecordMutationVariables,
  UpdateEmergencyVisitsEncounterRecordMutationVariables,
  DeleteEmergencyVisitRecordMutation,
  DeleteEmergencyVisitRecordMutationVariables,
} from 'generated/emergency-visit.generated';
import type { GetPatientHealthSummaryRecordsQuery } from 'generated/patient-record.generated';
import { GetPatientHealthSummaryRecordsDocument } from 'generated/patient-record.generated';

export const useGetEmergencyVisits = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetEmergencyVisitsRecordQuery({
    variables: { input: { patient: patient_id, count: 1500, page: 1 } },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);

  const records = (data?.listEmergencyVisitEncounters.data ?? []).map(
    (r) => new EncounterRecord(r)
  );
  return { ...response, data: records };
};

export const useGetEmergencyVisit = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetEmergencyVisitQuery({
    variables: { input: { ...params, patient_id } },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getEmergencyVisitEncounter;
  return { ...response, data: record ? new EncounterRecord(record) : null };
};

export function useCreateEmergencyVisit(): [
  (
    params: PostEmergencyVisitsRecordMutationVariables
  ) => Promise<FetchResult<EncounterRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostEmergencyVisitsRecordMutation();
  const loading = baseLoading;

  async function createEmergencyVisit(
    params: PostEmergencyVisitsRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postEmergencyVisitsEncounterRecord) {
          return;
        }
        const storeObject =
          response.postEmergencyVisitsEncounterRecord as unknown as StoreObject;
        const { encounter } = params.input;
        try {
          cache.modify<{ listEmergencyVisitEncounters: CachedRecordList }>({
            fields: {
              listEmergencyVisitEncounters: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === encounter.subject?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });

          cache.updateQuery<GetPatientHealthSummaryRecordsQuery>(
            {
              query: GetPatientHealthSummaryRecordsDocument,
              variables: {
                input: {
                  patient: encounter.subject?.reference,
                },
                emergency_visits_encounter: true,
              },
            },
            (data) => ({
              getPatientHealthSummaryRecords: {
                emergency_visits_encounter: [
                  ...(data?.getPatientHealthSummaryRecords
                    .emergency_visits_encounter ?? []),
                  response.postEmergencyVisitsEncounterRecord,
                ],
              },
            })
          );
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdEr = response.data?.postEmergencyVisitsEncounterRecord;
    return {
      ...response,
      data: createdEr ? new EncounterRecord(createdEr) : undefined,
    };
  }
  return [createEmergencyVisit, loading];
}

export function useUpdateEmergencyVisit(): [
  (
    params: UpdateEmergencyVisitsEncounterRecordMutationVariables
  ) => Promise<FetchResult<EncounterRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateEmergencyVisitsEncounterRecordMutation();
  const loading = baseLoading;

  async function updateEmergencyVisit(
    params: UpdateEmergencyVisitsEncounterRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedEr = response.data?.updateEmergencyVisitsEncounterRecord;
    return {
      ...response,
      data: updatedEr ? new EncounterRecord(updatedEr) : undefined,
    };
  }
  return [updateEmergencyVisit, loading];
}

export function useDeleteEmergencyVisit(): [
  (
    params: DeleteEmergencyVisitRecordMutationVariables
  ) => Promise<FetchResult<DeleteEmergencyVisitRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteEmergencyVisitRecordMutation();
  const loading = baseLoading;

  async function deleteEmergencyVisit(
    params: DeleteEmergencyVisitRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteEmergencyVisitRecord) {
          return;
        }
        const { encounter_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'EncounterModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteEmergencyVisit, loading];
}
